/*
  ========================================================================
  THEME: Impact v7.0.1 merge target + Gro unified rebuild
  STORES: Gro Clinics AU (groclinics.com.au) + NZ (groclinics.co.nz)
  LAST UPDATED: 23/09/2026
  PREVIOUSLY: 25/08/2026, 15/07/2026
  ========================================================================
  gro-video.js - shared, mobile-first video harness. Defines the custom
  element <gro-video> that snippets/gro-video.liquid emits, so no Gro
  section needs its own inline video script.

  ------------------------------------------------------------------------
  OWNERSHIP
  ------------------------------------------------------------------------
  OWNS   Player mounting, source selection, autoplay policy, the custom
         control surface (tap-anywhere toggle plus the control bar), the
         caption track and its toggle, the desktop hover preview, the
         playback milestone events, the single-player-at-a-time registry
         and focus handling.
  DOES NOT OWN  The markup (snippets/gro-video.liquid), the player surface
         styling (assets/gro-video.css), section chrome or width, article
         body token parsing (module - gro-article), or what any analytics
         consumer does with the milestone events once they are dispatched.

  ------------------------------------------------------------------------
  GRO MODULE CONTRACT - read before editing
  ------------------------------------------------------------------------
  BLAST RADIUS  Loaded from theme.liquid on every page of both stores and
    consumed by twenty-two files. There is no per-section copy. Treat every
    change as a storefront-wide change.
  DOM HOOKS  <gro-video>, .gro-video__activator, .gro-video__poster,
    .gro-video__play, script.gro-video__sources. The sources script is both
    the JSON payload and the mount anchor (insertBefore targets it), so it
    must remain the last child. The control layer adds .gro-video__ui and
    its children, all built here and all removed by restoreActivator.
  TAP CONTRACT  The whole player surface starts AND stops playback. The
    facade activator does that before mount; .gro-video__surface does it
    after. The surface is never hidden while a file player is mounted. This
    is the default for every consumer, not an opt-in mode.
  NATIVE CONTROLS  Off for mounted file players, because a transparent tap
    surface would swallow the native control bar. The bar built here
    replaces them. If buildControls throws, the catch restores native
    controls so the player is never left with no way to pause.
  RECONNECT SAFETY  The theme editor moves the SAME node on section
    reorder, firing disconnect then connect. One-time work is guarded by
    _init; listeners, the IntersectionObserver and registry membership
    re-attach on every connect. Anything added to connectedCallback must be
    idempotent or _init-guarded.
  AUTOPLAY LATCH  A rejected muted autoplay sets this.autoplay = false
    permanently and reveals the play affordance. Without that latch the
    element re-mounts on every viewport re-entry (iOS Low Power Mode).
  PAUSE INTENT  _userPaused is set ONLY from a control the visitor
    operated, via _intentPause. Every other pause (scroll-out, single-play,
    tab hide, Low Power Mode, audio focus loss) leaves it false so the
    observer can resume. Defaulting this the other way silently kills every
    ambient clip the moment the visitor switches apps.
  CAPTIONS  A <track kind="subtitles"> is appended only when the JSON
    payload carries a captions object, and only on a non-ambient file
    mount. The CC button is built only when that track exists, so a clip
    with no VTT gets no dead control. Provider iframes keep their own
    caption menus and ambient loops are decorative, so neither gets one.
    A cross-origin VTT needs video.crossOrigin set BEFORE the source
    loads, which is why captions are resolved above the src assignment.
  HOVER PREVIEW  Fine-pointer devices only, and only where the pointer
    event itself reports a mouse. Touch raises pointerenter too, so the
    matchMedia test alone would mount a player on every tap. A preview is
    muted, unlooped, capped by PREVIEW_SECONDS, carries no control layer
    and no analytics, never claims the single-play slot, and unwinds to
    the poster on pointer-out, scroll-out, disconnect or another player
    claiming the slot. A click promotes it in place rather than remounting.
  ANALYTICS  Milestone CustomEvents on the element (bubbling, composed)
    under ANALYTICS_EVENT, mirrored into window.dataLayer when GTM is
    present. Every milestone latches once per playback session. Previews
    and ambient loops are excluded on purpose: a decorative muted loop
    firing video_start on every page view is noise, not measurement.
    Nothing here may throw into the playback path.
  ACCESSIBILITY  playsinline is always set. The control bar carries the
    accessible names; the tap surface is exposed to assistive tech only
    when there is no bar, so a screen reader is not read two identical
    pause buttons. Focus is moved onto the mounted player and back to the
    activator on restore, never left on <body>.
  DOCTRINE  Modern JS only (const/let, arrow functions, optional chaining),
    CSS over JS, no em dashes. Log every edit in the module CHANGELOG.md
    and bump LAST UPDATED.

  ------------------------------------------------------------------------
  DEPENDENCIES
  ------------------------------------------------------------------------
  snippets/gro-video.liquid for the markup contract above, and
  assets/gro-video.css for the facade, control bar, preview, ambient and
  blocked-autoplay states (.gro-video--ambient, .gro-video--preview,
  .gro-video--show-play, .is-playing, .is-mounted, .is-idle, .is-buffering,
  .is-fullscreen are set here and styled there). No libraries. No
  metaobjects, metafields or app markup. window.dataLayer is read
  defensively if GTM put one there and is never created.

  ------------------------------------------------------------------------
  DELIBERATE EXEMPTIONS
  ------------------------------------------------------------------------
  None. The marked HLS.js hook in selectSource is an unwired extension
  point, not an exemption.

  ------------------------------------------------------------------------
  Mobile-first behaviour:
   - Poster facade: no <video> in the DOM until activation, so the poster
     stays the LCP candidate and no video bytes load up front.
   - playsinline is always set (stops iOS forcing fullscreen).
   - The whole surface is the play AND pause target, at every size. The
     control bar auto-hides during playback and returns on any interaction.
   - Adaptive source: native HLS (.m3u8) where supported (Safari and iOS
     get true adaptive bitrate). Other browsers get the mp4 rendition
     closest to the rendered size and connection, so phones do not pull a
     1080p file. See selectProgressiveSource for the HLS.js extension hook.
   - Autoplay only when muted, in view, not reduced-motion, not Save-Data.
     Pauses when scrolled out of view to save battery and data, and resumes
     on return, including after a tab switch.
   - The hover preview is a desktop-only addition and never fires on a
     phone: touch pointer events are rejected on top of the fine-pointer
     media query, and reduced motion or Save-Data suppress it outright.
   - One video plays at a time across the page.
   - Theme-editor safe: connected/disconnected callbacks handle re-render.
  ========================================================================
*/

(() => {
  if (window.customElements && customElements.get('gro-video')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // A mouse, a trackpad or a stylus with a real hover state. A phone, a
  // tablet and a touchscreen laptop in touch mode all fail this, which is
  // what keeps the hover preview off the devices that pay for the bytes.
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;

  // How long the control bar stays up after the last interaction, while
  // playing. Paused players always show it.
  const IDLE_MS = 2600;

  // How much of a clip a hover preview is allowed to stream before it stops
  // on its own. Capped by the media's own currentTime rather than a timer,
  // so a stalled or throttled preview never overruns.
  const PREVIEW_SECONDS = 6;

  // One stable DOM event name for every playback milestone. Listeners read
  // detail.milestone ('play' | 'progress' | 'complete') and detail.percent
  // (0 | 25 | 50 | 75 | 100) rather than binding several names.
  const ANALYTICS_EVENT = 'gro-video:progress';

  // GA4's own video vocabulary, so a GTM trigger needs no translation layer.
  const DATALAYER_EVENT = {
    play: 'video_start',
    progress: 'video_progress',
    complete: 'video_complete'
  };

  const prefersLightData = () => {
    if (!connection) return false;
    if (connection.saveData) return true;
    return /(^|-)2g$/.test(connection.effectiveType || '');
  };

  // A VTT on another origin is only readable when the media element opts
  // into CORS first, so the caller needs to know before the source is set.
  const isCrossOrigin = (src) => {
    try {
      return new URL(src, window.location.href).origin !== window.location.origin;
    } catch (err) {
      return false;
    }
  };

  // GTM may not be on the page, may load later, or may have been blocked by
  // a consent tool that left a stub behind. None of that is allowed to reach
  // the playback path, so every branch here fails silently.
  const pushDataLayer = (milestone, detail) => {
    try {
      const layer = window.dataLayer;
      if (!layer || typeof layer.push !== 'function') return;
      layer.push({
        event: DATALAYER_EVENT[milestone] || DATALAYER_EVENT.progress,
        video_provider: 'gro-video',
        video_percent: detail.percent,
        video_current_time: detail.currentTime,
        video_duration: detail.duration,
        video_url: detail.src,
        video_title: detail.title
      });
    } catch (err) {
      // Measurement is never worth a broken player.
    }
  };

  // Page-wide registry so only one player runs at a time. Ambient clips
  // (muted background loops) neither claim the slot nor get paused by it.
  const registry = new Set();
  const pauseOthers = (active) => {
    registry.forEach((instance) => {
      if (instance !== active && !instance.isAmbient) instance.pauseMedia();
    });
  };

  // A tab switch pauses muted ambient clips at the browser's own
  // discretion. That pause is not the visitor's, so on return the clip has
  // to be re-armed by hand: the IntersectionObserver does not re-fire for
  // an element that never left the viewport.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    registry.forEach((instance) => instance.resumeAmbient());
  });

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    if (mins < 60) return `${mins}:${String(secs).padStart(2, '0')}`;
    const hours = Math.floor(mins / 60);
    return `${hours}:${String(mins % 60).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const ICONS = {
    play: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="7,4 20,12 7,20"/></svg>',
    pause: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>',
    muted: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 9h3l4-4v14l-4-4H4z"/><path d="M15.5 9.5l4 4m0-4l-4 4" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>',
    unmuted: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 9h3l4-4v14l-4-4H4z"/><path d="M15 8.5a4.5 4.5 0 0 1 0 7M17.5 6a8 8 0 0 1 0 12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>',
    enterFs: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
    exitFs: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"/></svg>',
    // Drawn as paths, not as <text>: a glyph would need a font-family, and
    // this estate does not write one as a literal. The on and off states are
    // carried by aria-pressed and painted in CSS, so there is one icon.
    cc: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2.6" y="5.2" width="18.8" height="13.6" rx="3"/><path d="M10.4 10.3a2.9 2.9 0 1 0 0 3.4"/><path d="M17.6 10.3a2.9 2.9 0 1 0 0 3.4"/></svg>'
  };

  class GroVideo extends HTMLElement {
    connectedCallback() {
      // One-time setup, then per-connect wiring: the theme editor moves the
      // SAME node on section reorder (disconnect + reconnect), so listeners,
      // observer and registry must re-attach on every connect.
      if (!this._init) {
        this._init = true;
        this.config = this.readConfig();
        this.activator = this.querySelector('.gro-video__activator');
        this.media = null;
        this.ui = null;
        this.frameLoaded = false;
        this.io = null;
        this.onActivate = this.onActivate.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onFullscreenChange = this.onFullscreenChange.bind(this);
        this.onPointerEnter = this.onPointerEnter.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this._activatorLabel = this.activator ? this.activator.getAttribute('aria-label') : '';
        this.resetMilestones();

        const isProvider = this.config && (this.config.type === 'youtube' || this.config.type === 'vimeo' || this.config.type === 'tolstoy');
        this.isProvider = isProvider;
        // Ambient clips (muted background loops) opt out of the single-play rules.
        this.isAmbient = this.dataset.autoplay === 'true';
        this.autoplay = this.isAmbient && !isProvider && !reducedMotion.matches && !prefersLightData();
        // Merchant intent for a visible control bar. The tap surface is not
        // negotiable and is present either way.
        this.wantsBar = this.dataset.controls !== 'false';
      }
      if (!this.config) return;

      if (this.activator) this.activator.addEventListener('click', this.onActivate);
      this.addEventListener('keydown', this.onKeydown);
      // pointerenter/pointerleave, not over/out: they do not bubble from
      // descendants, so moving between the poster and the play glyph does
      // not re-enter and re-mount. Both are cheap to attach and the handlers
      // reject everything that is not a fine-pointer mouse.
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
      this.setupObserver();
      registry.add(this);
    }

    disconnectedCallback() {
      registry.delete(this);
      if (this.io) { this.io.disconnect(); this.io = null; }
      if (this.activator && this.onActivate) this.activator.removeEventListener('click', this.onActivate);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
      this.clearIdleTimer();
      this.pauseMedia();
    }

    readConfig() {
      const node = this.querySelector('.gro-video__sources');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (err) {
        return null;
      }
    }

    setupObserver() {
      if (!('IntersectionObserver' in window)) {
        if (this.autoplay) this.activate(true);
        return;
      }
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._inView = true;
            if (this.media) {
              // Never resume over a deliberate user pause.
              if (this.autoplay && this.media.paused && !this._userPaused) this.media.play().catch(() => {});
            } else if (this.autoplay) {
              this.activate(true);
            }
          } else {
            this._inView = false;
            if (this.media && !this.media.paused) this.pauseMedia();
          }
        });
      }, { threshold: 0.25, rootMargin: '200px 0px' });
      this.io.observe(this);
    }

    // Called for every instance when the tab becomes visible again.
    resumeAmbient() {
      if (!this.autoplay || !this.media || this._userPaused) return;
      if (this._inView === false) return;
      if (!this.media.paused) return;
      this.media.play().catch(() => {});
    }

    onActivate(event) {
      event.preventDefault();
      // A hover preview leaves this.media set while the facade is still on
      // top. Without this branch the click would land on togglePlayback and
      // PAUSE the preview, which is the opposite of what a click means here.
      if (this._preview) {
        this.promotePreview();
        return;
      }
      if (this.media) {
        this.togglePlayback();
        return;
      }
      this.activate(false);
    }

    /* ------------------------------------------------------------------
       Desktop hover preview. Mounts a muted, unlooped, controlless clip
       behind the facade and unwinds it the moment the pointer leaves.
       ------------------------------------------------------------------ */

    canPreview(event) {
      if (!this.config) return false;
      if (this._preview || this.media || this.frameLoaded) return false;
      // Ambient clips already play. A provider preview would mean mounting a
      // YouTube iframe on hover, which is hostile and cannot be unwound.
      if (this.isAmbient || this.isProvider) return false;
      // Touch raises pointerenter before click, so the media query alone
      // would mount a player on every tap. Reject anything but a mouse.
      if (event && event.pointerType && event.pointerType !== 'mouse') return false;
      if (!finePointer.matches) return false;
      // Same two gates the ambient autoplay path already respects.
      if (reducedMotion.matches || prefersLightData()) return false;
      return true;
    }

    onPointerEnter(event) {
      if (!this.canPreview(event)) return;
      this.activate(false, true);
    }

    onPointerLeave() {
      this.endPreview();
    }

    // Unwind to the poster. Safe to call when no preview is running, which
    // is why the observer, the registry and disconnect can all route here.
    endPreview() {
      if (!this._preview) return;
      this._preview = false;
      this.classList.remove('gro-video--preview', 'is-playing');
      if (this.media && !this.media.paused) this.media.pause();
      this.restoreActivator();
      this._userPaused = false;
      this._intentPause = false;
      this.resetMilestones();
    }

    // A click on a previewing player keeps the bytes already fetched: the
    // same <video> gains sound, the loop setting, the control layer and a
    // fresh analytics session rather than being torn down and remounted.
    promotePreview() {
      const video = this.media;
      if (!video) return;
      this._preview = false;
      this.classList.remove('gro-video--preview');
      // The preview deliberately did not claim the single-play slot. Now
      // that it is real playback it must, and the 'play' handler cannot do
      // it: the element is ALREADY playing, so play() below resolves without
      // firing a play event. Measured - another clip kept running.
      pauseOthers(this);
      video.loop = this.dataset.loop === 'true';
      video.muted = this.dataset.muted === 'true';
      video.currentTime = 0;
      this.resetMilestones();
      if (this.activator) {
        this.activator.hidden = true;
        this.activator.setAttribute('aria-hidden', 'true');
      }
      try {
        this.buildControls(video);
      } catch (err) {
        // Same guarantee as the cold mount: never a player with no controls.
        this.teardownControls();
        video.controls = true;
      }
      this.classList.add('is-mounted');
      const focusTarget = this.playButton || this.surface || video;
      focusTarget.focus({ preventScroll: true });
      // play() on an element that is already playing resolves without firing
      // a 'play' event, so the milestone has to be raised by hand here. The
      // latch in emitMilestone absorbs the duplicate when it does fire.
      this.emitMilestone('play', 0);
      const playback = video.play();
      if (playback && typeof playback.catch === 'function') {
        playback.catch(() => {});
      }
    }

    // The one place playback is flipped by something the visitor operated,
    // and therefore the one place _intentPause is raised.
    togglePlayback() {
      if (!this.media) return;
      if (this.media.paused) {
        this.media.play().catch(() => {});
        this.flash('play');
      } else {
        this._intentPause = true;
        this.media.pause();
        this.flash('pause');
      }
      this.wake();
    }

    activate(isAuto, isPreview) {
      if (this.media || this.frameLoaded) return;
      const type = this.config.type;
      if (type === 'youtube' || type === 'vimeo' || type === 'tolstoy') {
        // A provider iframe cannot be previewed and unwound, so hover does
        // nothing here and the facade waits for a real click.
        if (isPreview) return;
        this.mountIframe(type);
        return;
      }
      this.mountVideo(isAuto, isPreview);
    }

    mountIframe(type) {
      const src = this.embedUrl(type, this.config.src);
      if (!src) return;
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; clipboard-write');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', this.getAttribute('data-title') || 'Video');
      // A provider iframe owns its own controls; the harness surface would
      // only sit in their way, so the facade is hidden as before.
      this.replaceActivator(iframe);
      this.frameLoaded = true;
      // Hiding the focused activator would drop focus to body.
      iframe.focus({ preventScroll: true });
    }

    mountVideo(isAuto, isPreview) {
      const video = document.createElement('video');
      video.className = 'gro-video__media';
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'metadata';

      const posterImg = this.querySelector('img.gro-video__poster');
      const posterSrc = posterImg ? (posterImg.currentSrc || posterImg.src) : '';
      if (posterSrc) video.poster = posterSrc;

      // A preview must not loop, or currentTime resets and the
      // PREVIEW_SECONDS cap below can never be reached.
      video.loop = isPreview ? false : this.dataset.loop === 'true';
      // Autoplay is only allowed muted, so force mute on the auto path. A
      // hover preview is muted for the same reason and because sound the
      // visitor did not ask for is its own defect.
      video.muted = isAuto || isPreview ? true : this.dataset.muted === 'true';
      // Native controls are deliberately OFF for the tap path: the tap
      // surface covers the whole player and would swallow them. The bar
      // built below replaces them. Ambient loops never had them either.
      video.controls = false;

      const source = this.selectSource(video);
      if (!source) return;

      // Captions are resolved BEFORE the source is set. A VTT on another
      // origin is only readable when the element opted into CORS, and an
      // opt-in applied after the load has begun has no effect. Scoped to
      // caption consumers so a plain clip never pays the CORS cost.
      const captions = isAuto ? null : this.captionConfig();
      if (captions && isCrossOrigin(captions.src)) video.crossOrigin = 'anonymous';

      video.src = source;

      this.hasCaptions = false;
      if (captions) {
        const track = document.createElement('track');
        track.kind = 'subtitles';
        track.label = captions.label;
        track.srclang = captions.lang;
        track.src = captions.src;
        video.appendChild(track);
        // Left at the browser's default 'disabled', so the VTT is not
        // fetched until the visitor presses CC. The toggle then moves it
        // between 'showing' and 'hidden' for the rest of the session.
        this.hasCaptions = true;
      }

      const title = this.getAttribute('data-title');
      if (title) video.setAttribute('aria-label', title);

      this.resetMilestones();
      this.bindMediaEvents(video);

      if (isPreview) {
        // The facade stays exactly where it is and keeps the click. Only the
        // poster clears, so the muted frame shows through; the play glyph
        // stays up because a preview is a peek, not playback. No control
        // layer, no is-mounted, nothing for endPreview to have to undo
        // beyond removing the media and restoring the poster.
        this._preview = true;
        this.classList.add('gro-video--preview');
        this.insertBefore(video, this.querySelector('.gro-video__sources'));
        this.media = video;
      } else if (isAuto) {
        // Ambient mount: the facade activator stays above the clip as a
        // transparent pause/play toggle (WCAG 2.2.2 - looping media must be
        // pausable). No bar: this is decorative background, not a player.
        this.classList.add('gro-video--ambient');
        if (this.activator) this.activator.setAttribute('aria-label', 'Pause video');
        this.insertBefore(video, this.querySelector('.gro-video__sources'));
        this.media = video;
      } else {
        // Tap path: the poster facade steps aside and the control layer
        // takes over the surface, so the player never stops being tappable.
        if (this.activator) {
          this.activator.hidden = true;
          this.activator.setAttribute('aria-hidden', 'true');
        }
        this.insertBefore(video, this.querySelector('.gro-video__sources'));
        this.media = video;
        try {
          this.buildControls(video);
        } catch (err) {
          // Never leave a player the visitor cannot pause.
          this.teardownControls();
          video.controls = true;
        }
        this.classList.add('is-mounted');
        const focusTarget = this.playButton || this.surface || video;
        focusTarget.focus({ preventScroll: true });
      }

      const playback = video.play();
      if (playback && typeof playback.catch === 'function') {
        playback.catch((err) => {
          if (isPreview) {
            // A refused preview is a non-event. Unwind to the poster and
            // leave no trace: the visitor never asked for this mount.
            this.endPreview();
            return;
          }
          if (isAuto) {
            // Blocked muted autoplay (e.g. iOS Low Power Mode) would otherwise
            // re-mount on every viewport re-entry, so stop auto attempts for
            // good and reveal the play affordance the autoplay styling hides.
            this.autoplay = false;
            this.classList.remove('gro-video--ambient');
            if (this.activator && this._activatorLabel) this.activator.setAttribute('aria-label', this._activatorLabel);
            this.restoreActivator();
            this.classList.add('gro-video--show-play');
            return;
          }
          // User-initiated: only restore the facade if the browser refused the
          // gesture outright. On other rejections (AbortError races, power
          // saving) keep the mounted player so its controls still work.
          if (err && err.name === 'NotAllowedError') {
            this.restoreActivator();
            // Removing the focused video would strand focus on body.
            if (this.activator) this.activator.focus({ preventScroll: true });
          }
        });
      }
    }

    /* ------------------------------------------------------------------
       Control layer. Built on mount, torn down by restoreActivator.
       ------------------------------------------------------------------ */

    buildControls(video) {
      const ui = document.createElement('div');
      ui.className = 'gro-video__ui';

      // The tap surface. This is the headline behaviour: the whole player
      // starts and stops, at every breakpoint, exactly as the facade does.
      //
      // It is a <button> ONLY when it is the sole control. With a bar
      // present the bar carries the accessible names, and a second focusable
      // "Pause video" over the whole player would be read twice and add a
      // dead stop to the tab order. Marking a <button> aria-hidden does not
      // solve that: a click still focuses it, and a focused aria-hidden
      // element is an outright violation that Chrome logs. A plain <div> is
      // not focusable, so the touch affordance and the accessible control
      // stay cleanly separate.
      const hasBar = this.wantsBar;
      const surface = document.createElement(hasBar ? 'div' : 'button');
      if (!hasBar) surface.type = 'button';
      surface.className = 'gro-video__surface';
      if (hasBar) surface.setAttribute('aria-hidden', 'true');
      surface.addEventListener('click', (event) => {
        event.preventDefault();
        this.togglePlayback();
      });
      ui.appendChild(surface);
      this.surface = surface;

      // Transient centre glyph so a tap reads as a deliberate state change
      // rather than as a stall.
      const feedback = document.createElement('span');
      feedback.className = 'gro-video__feedback';
      feedback.setAttribute('aria-hidden', 'true');
      ui.appendChild(feedback);
      this.feedback = feedback;

      const spinner = document.createElement('span');
      spinner.className = 'gro-video__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      ui.appendChild(spinner);

      if (this.wantsBar) {
        ui.appendChild(this.buildBar(video));
      } else {
        surface.setAttribute('aria-label', 'Pause video');
      }

      this.insertBefore(ui, this.querySelector('.gro-video__sources'));
      this.ui = ui;
      this.syncControls();
      this.wake();
    }

    buildBar(video) {
      const bar = document.createElement('div');
      bar.className = 'gro-video__bar';
      // A pointerdown on the bar must not reach the surface underneath, or
      // every scrub and every mute would also toggle playback.
      bar.addEventListener('click', (event) => event.stopPropagation());

      const playBtn = document.createElement('button');
      playBtn.type = 'button';
      playBtn.className = 'gro-video__btn gro-video__btn--play';
      playBtn.addEventListener('click', () => this.togglePlayback());
      bar.appendChild(playBtn);
      this.playButton = playBtn;

      const scrub = document.createElement('input');
      scrub.type = 'range';
      scrub.className = 'gro-video__scrub';
      scrub.min = '0';
      scrub.max = '1000';
      scrub.step = '1';
      scrub.value = '0';
      scrub.setAttribute('aria-label', 'Seek');
      // A normalised 0 to 1000 range avoids re-setting max when duration
      // arrives late or is Infinity on a live source. aria-valuetext carries
      // the meaning for assistive tech.
      scrub.addEventListener('input', () => {
        this._scrubbing = true;
        const duration = this.media?.duration;
        if (Number.isFinite(duration)) {
          this.media.currentTime = (Number(scrub.value) / 1000) * duration;
        }
        this.wake();
      });
      scrub.addEventListener('change', () => { this._scrubbing = false; });
      bar.appendChild(scrub);
      this.scrub = scrub;

      const time = document.createElement('span');
      time.className = 'gro-video__time';
      bar.appendChild(time);
      this.timeLabel = time;

      const muteBtn = document.createElement('button');
      muteBtn.type = 'button';
      muteBtn.className = 'gro-video__btn gro-video__btn--mute';
      muteBtn.addEventListener('click', () => {
        video.muted = !video.muted;
        this.syncControls();
        this.wake();
      });
      bar.appendChild(muteBtn);
      this.muteButton = muteBtn;

      // Built only when a track actually exists. A CC button on a clip with
      // no VTT is a control that does nothing, which is worse than no
      // control: turning native controls off removed the browser's own
      // caption menu, so this is now the only way captions can be reached.
      if (this.hasCaptions) {
        const ccBtn = document.createElement('button');
        ccBtn.type = 'button';
        ccBtn.className = 'gro-video__btn gro-video__btn--cc';
        ccBtn.innerHTML = ICONS.cc;
        ccBtn.setAttribute('aria-pressed', 'false');
        ccBtn.addEventListener('click', () => {
          this.toggleCaptions();
          this.wake();
        });
        bar.appendChild(ccBtn);
        this.ccButton = ccBtn;
      }

      const fsBtn = document.createElement('button');
      fsBtn.type = 'button';
      fsBtn.className = 'gro-video__btn gro-video__btn--fs';
      fsBtn.addEventListener('click', () => this.toggleFullscreen());
      bar.appendChild(fsBtn);
      this.fsButton = fsBtn;

      return bar;
    }

    teardownControls() {
      if (this.ui) this.ui.remove();
      this.ui = null;
      this.surface = null;
      this.feedback = null;
      this.playButton = null;
      this.scrub = null;
      this.timeLabel = null;
      this.muteButton = null;
      this.ccButton = null;
      this.fsButton = null;
      this.clearIdleTimer();
      this.classList.remove('is-mounted', 'is-idle', 'is-buffering');
    }

    /* ------------------------------------------------------------------
       Captions. One track, one toggle, and only when a VTT was supplied.
       ------------------------------------------------------------------ */

    captionConfig() {
      const cap = this.config && this.config.captions;
      if (!cap || !cap.src) return null;
      return {
        src: cap.src,
        label: cap.label || 'English',
        lang: cap.lang || 'en'
      };
    }

    toggleCaptions() {
      const track = this.media && this.media.textTracks && this.media.textTracks[0];
      if (!track) return;
      // The track starts at the browser's default 'disabled' so the VTT is
      // not fetched until it is wanted. From the first press on, it moves
      // between the two states a visitor can perceive.
      track.mode = track.mode === 'showing' ? 'hidden' : 'showing';
      this.syncControls();
    }

    syncControls() {
      const video = this.media;
      if (!video) return;
      const playing = !video.paused;

      if (this.playButton) {
        this.playButton.innerHTML = playing ? ICONS.pause : ICONS.play;
        this.playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      }
      if (this.surface && !this.wantsBar) {
        this.surface.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      }
      if (this.muteButton) {
        this.muteButton.innerHTML = video.muted ? ICONS.muted : ICONS.unmuted;
        this.muteButton.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
      }
      if (this.ccButton) {
        // One icon, two states. aria-pressed carries the state for assistive
        // tech AND is the hook the CSS paints the on state from, so there is
        // no second class to keep in step.
        const track = video.textTracks && video.textTracks[0];
        const on = !!track && track.mode === 'showing';
        this.ccButton.setAttribute('aria-pressed', on ? 'true' : 'false');
        this.ccButton.setAttribute('aria-label', on ? 'Hide captions' : 'Show captions');
      }
      if (this.fsButton) {
        const isFs = this.isFullscreen();
        this.fsButton.innerHTML = isFs ? ICONS.exitFs : ICONS.enterFs;
        this.fsButton.setAttribute('aria-label', isFs ? 'Exit full screen' : 'Full screen');
      }
      this.syncProgress();
    }

    syncProgress() {
      const video = this.media;
      if (!video) return;
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const pct = duration ? (video.currentTime / duration) * 100 : 0;

      if (this.scrub && !this._scrubbing) {
        this.scrub.value = String(Math.round((pct / 100) * 1000));
        this.scrub.setAttribute('aria-valuetext', `${formatTime(video.currentTime)} of ${formatTime(duration)}`);
      }
      let buffered = 0;
      if (duration && video.buffered.length) {
        buffered = (video.buffered.end(video.buffered.length - 1) / duration) * 100;
      }
      // CSS draws the fill and the buffer from these; JS does not touch paint.
      this.style.setProperty('--gro-video-progress', pct.toFixed(2));
      this.style.setProperty('--gro-video-buffer', buffered.toFixed(2));

      if (this.timeLabel) {
        this.timeLabel.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
      }
    }

    // Reveal the bar and restart the idle countdown. A paused player keeps
    // its bar up: there is nothing to watch behind it.
    wake() {
      this.classList.remove('is-idle');
      this.clearIdleTimer();
      if (!this.media || this.media.paused) return;
      this._idleTimer = window.setTimeout(() => {
        this.classList.add('is-idle');
      }, IDLE_MS);
    }

    clearIdleTimer() {
      if (this._idleTimer) {
        window.clearTimeout(this._idleTimer);
        this._idleTimer = null;
      }
    }

    flash(kind) {
      if (!this.feedback || reducedMotion.matches) return;
      this.feedback.innerHTML = kind === 'pause' ? ICONS.pause : ICONS.play;
      // Restarting a CSS animation needs the class to actually drop a frame.
      this.feedback.classList.remove('is-flash');
      void this.feedback.offsetWidth;
      this.feedback.classList.add('is-flash');
    }

    onKeydown(event) {
      if (!this.media) return;
      // Let the range input own its own arrow keys.
      if (event.target === this.scrub && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) return;
      const video = this.media;
      switch (event.key) {
        case ' ':
        case 'k':
        case 'K':
          event.preventDefault();
          this.togglePlayback();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          video.currentTime = Math.max(0, video.currentTime - 5);
          this.wake();
          break;
        case 'ArrowRight':
          event.preventDefault();
          video.currentTime = Math.min(video.duration || 0, video.currentTime + 5);
          this.wake();
          break;
        case 'm':
        case 'M':
          event.preventDefault();
          video.muted = !video.muted;
          this.syncControls();
          this.wake();
          break;
        case 'c':
        case 'C':
          // Only meaningful when a track exists; otherwise the key falls
          // through to the page like any other unbound key.
          if (!this.hasCaptions) break;
          event.preventDefault();
          this.toggleCaptions();
          this.wake();
          break;
        case 'f':
        case 'F':
          event.preventDefault();
          this.toggleFullscreen();
          break;
        default:
          break;
      }
    }

    isFullscreen() {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      return fsEl === this || (!!fsEl && this.contains(fsEl));
    }

    toggleFullscreen() {
      this.wake();
      if (this.isFullscreen()) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) exit.call(document);
        return;
      }
      // iPhone Safari has no element fullscreen, only the video's own native
      // presentation. That hands over to the native player, controls and all,
      // which is the correct behaviour there rather than a broken button.
      if (!document.fullscreenEnabled && this.media?.webkitEnterFullscreen) {
        this.media.webkitEnterFullscreen();
        return;
      }
      const request = this.requestFullscreen || this.webkitRequestFullscreen;
      if (request) request.call(this);
    }

    onFullscreenChange() {
      this.classList.toggle('is-fullscreen', this.isFullscreen());
      this.syncControls();
    }

    selectSource(video) {
      const config = this.config;
      if (config.type !== 'file') return config.src || '';

      const sources = Array.isArray(config.sources) ? config.sources : [];
      if (!sources.length) return '';

      const hls = sources.find((s) => (s.type || '').indexOf('mpegurl') !== -1 || (s.url || '').indexOf('.m3u8') !== -1);
      const progressive = sources.filter((s) => s !== hls && s.url);

      // Safari and iOS play HLS natively and adapt bitrate to the network.
      if (hls && video.canPlayType('application/vnd.apple.mpegurl')) return hls.url;

      const picked = this.selectProgressiveSource(progressive);
      if (picked) return picked;

      // HLS.js hook: load hls.js and attach it to hls.url here to give
      // non-Safari browsers true adaptive bitrate. Native HLS plus the
      // right-sized mp4 below cover the common cases without the dependency.
      return hls ? hls.url : '';
    }

    selectProgressiveSource(progressive) {
      if (!progressive.length) return '';

      const sorted = progressive
        .map((s) => ({ url: s.url, width: Number(s.width) || 0 }))
        .sort((a, b) => a.width - b.width);

      // Cap DPR at 2: past that the bitrate cost outweighs the gain on video.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let target = (this.clientWidth || window.innerWidth || 360) * dpr;

      const eff = connection && connection.effectiveType ? connection.effectiveType : '';
      if (connection && connection.saveData) target = Math.min(target, 480);
      else if (/(^|-)2g$/.test(eff)) target = Math.min(target, 480);
      else if (eff === '3g') target = Math.min(target, 720);

      const fit = sorted.find((s) => s.width >= target);
      return (fit || sorted[sorted.length - 1]).url;
    }

    /* ------------------------------------------------------------------
       Playback milestones. One stable DOM event, plus a GTM push when a
       dataLayer exists. No third-party code and nothing that can throw
       into playback.
       ------------------------------------------------------------------ */

    resetMilestones() {
      // Keyed by percent so the play (0) and complete (100) latches share
      // the quartile mechanism instead of needing their own flags.
      this._fired = { 0: false, 25: false, 50: false, 75: false, 100: false };
    }

    emitMilestone(milestone, percent) {
      if (!this._fired) this.resetMilestones();
      if (this._fired[percent]) return;
      // A preview is not a play, and an ambient loop is decoration. Counting
      // either would drown the clips a visitor actually chose to watch.
      if (this._preview || this.isAmbient) return;
      this._fired[percent] = true;

      const video = this.media;
      const duration = video && Number.isFinite(video.duration) ? video.duration : 0;
      const detail = {
        milestone,
        percent,
        currentTime: video ? Math.round(video.currentTime * 100) / 100 : 0,
        duration: Math.round(duration * 100) / 100,
        // currentSrc is the resolved one and is what a report should carry;
        // src is the fallback for the window before resource selection ends.
        src: (video && (video.currentSrc || video.src)) || (this.config && this.config.src) || '',
        title: this.getAttribute('data-title') || ''
      };

      // Bubbling and composed so a section wrapper, a page script or a
      // listener outside a shadow root can all bind one handler.
      this.dispatchEvent(new CustomEvent(ANALYTICS_EVENT, {
        detail,
        bubbles: true,
        composed: true
      }));
      pushDataLayer(milestone, detail);
    }

    trackProgress() {
      const video = this.media;
      if (!video || this._preview || this.isAmbient) return;
      const duration = video.duration;
      // Duration is NaN before metadata and Infinity on a live source, and
      // dividing by either produces a percent nobody should act on.
      if (!Number.isFinite(duration) || duration <= 0) return;
      const pct = (video.currentTime / duration) * 100;
      // Cumulative and in order, not a single else-if chain. timeupdate
      // fires about four times a second, so straight playback never skips a
      // threshold; a SEEK does, and a 100 with no 75 behind it makes the
      // funnel unreadable. Firing every threshold reached keeps it monotonic,
      // and the latch still holds each one to once per session.
      if (pct >= 25) this.emitMilestone('progress', 25);
      if (pct >= 50) this.emitMilestone('progress', 50);
      if (pct >= 75) this.emitMilestone('progress', 75);
    }

    bindMediaEvents(video) {
      video.addEventListener('play', () => {
        this._userPaused = false;
        // A preview never claims the single-play slot: it would pause the
        // clip the visitor is deliberately watching somewhere else on the
        // page, for a hover they may not even have meant.
        if (!this.isAmbient && !this._preview) pauseOthers(this);
        if (this.isAmbient && this.activator) this.activator.setAttribute('aria-label', 'Pause video');
        this.classList.add('is-playing');
        this.syncControls();
        this.wake();
      });

      video.addEventListener('pause', () => {
        this.classList.remove('is-playing');
        if (this.isAmbient && this.activator) this.activator.setAttribute('aria-label', this._activatorLabel || 'Play video');
        // A pause belongs to the visitor ONLY when they operated a control.
        // Scroll-out, single-play, tab hide, Low Power Mode and audio focus
        // loss all land here too, and treating those as intent would leave
        // the clip dead for the rest of the page view.
        this._userPaused = this._intentPause === true;
        this._intentPause = false;
        this.syncControls();
        this.wake();
      });

      video.addEventListener('timeupdate', () => {
        // The preview stops itself off the media clock rather than a timer,
        // so a stalled or throttled preview cannot overrun its cap.
        if (this._preview && video.currentTime >= PREVIEW_SECONDS) {
          this.endPreview();
          return;
        }
        this.syncProgress();
        this.trackProgress();
      });
      video.addEventListener('progress', () => this.syncProgress());
      video.addEventListener('loadedmetadata', () => this.syncControls());
      video.addEventListener('volumechange', () => this.syncControls());
      video.addEventListener('waiting', () => this.classList.add('is-buffering'));
      video.addEventListener('playing', () => {
        this.classList.remove('is-buffering');
        // The play milestone rides 'playing', not 'play'. 'play' fires the
        // instant the element is ASKED to start, before resource selection
        // has populated currentSrc and before duration is known, so it
        // reported a start with an empty src and a zero duration - measured,
        // not theorised. 'playing' fires once frames are actually moving,
        // and the latch absorbs every later resume.
        this.emitMilestone('play', 0);
      });
      video.addEventListener('canplay', () => this.classList.remove('is-buffering'));
      video.addEventListener('ended', () => {
        this.classList.remove('is-playing', 'is-idle');
        this.emitMilestone('complete', 100);
        // A replay is a new playback session. A looping clip never reaches
        // here, so it reports its quartiles once and never completes, which
        // is the honest reading of a clip that has no end.
        this.resetMilestones();
        this.syncControls();
      });
      video.addEventListener('pointermove', () => this.wake());
    }

    replaceActivator(node) {
      if (this.activator) {
        this.activator.hidden = true;
        this.activator.setAttribute('aria-hidden', 'true');
      }
      this.insertBefore(node, this.querySelector('.gro-video__sources'));
    }

    restoreActivator() {
      this.teardownControls();
      if (this.media) {
        this.media.remove();
        this.media = null;
      }
      if (this.activator) {
        this.activator.hidden = false;
        this.activator.removeAttribute('aria-hidden');
      }
    }

    pauseMedia() {
      // A paused preview would leave the poster hidden behind a frozen
      // frame with no control to restart it. Unwind instead, so scroll-out,
      // another player claiming the slot and disconnect all land somewhere
      // the visitor can act on.
      if (this._preview) {
        this.endPreview();
        return;
      }
      if (this.media && !this.media.paused) {
        // Deliberately does NOT raise _intentPause: this is the harness
        // pausing on the visitor's behalf, not the visitor pausing.
        this.media.pause();
      }
    }

    embedUrl(type, raw) {
      if (!raw) return '';
      if (type === 'youtube') {
        const match = raw.match(/(?:youtu\.be\/|v=|embed\/|shorts\/|live\/)([\w-]+)/);
        return match ? 'https://www.youtube.com/embed/' + match[1] + '?autoplay=1&rel=0&playsinline=1' : '';
      }
      if (type === 'vimeo') {
        const match = raw.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        return match ? 'https://player.vimeo.com/video/' + match[1] + '?autoplay=1' : '';
      }
      // Tolstoy player URL is used as-is.
      return raw;
    }
  }

  customElements.define('gro-video', GroVideo);
})();
